import clsx from 'clsx'

import { useDarkMode } from '../DarkMode/hook'
import appDefaults from '~/app.defaults.json'
import { ProductTileAction } from '~/components/ProductTile/ProductTileAction'
import { ProductWrapper } from '~/components/ProductTile/ProductWrapper'
import { themeColors } from '~/styles/themeColors.mjs'
import { LandingProduct } from '~/types/product'

export interface ProductTileProps {
  product: LandingProduct;
  accentColor?: string;
  href: string;
  fontFamily?: string;
  disabled?: boolean;
  isPreview?: boolean;
  loggedIn?: boolean;
  portalName?: string;
  userFullName?: string;
  className?: string;
  // provider side uses desktop view
  responsive?: boolean;
}

export const ProductTile = ({
  product,
  accentColor,
  href,
  fontFamily,
  disabled = false,
  isPreview,
  loggedIn,
  portalName,
  userFullName,
  className,
  responsive,
}: ProductTileProps) => {
  const { isDark } = useDarkMode()
  const { slug, name, description, logoImageHref, logoImageDarkModeHref, canAccess } = product

  const backgroundColor = canAccess ?
    accentColor ?? themeColors['accent-60'] :
    !isPreview ? isDark() ? themeColors['neutral-85'] : themeColors['neutral-20']
      : undefined

  return (
    <ProductWrapper
      href={href}
      slug={slug}
      disabled={!canAccess || isPreview || disabled}
      className={'max-w-[calc(100vw-3rem)] md:max-w-none'}
    >
      <div
        className={clsx(
          'group overflow-hidden rounded-5 bg-white dark:bg-neutral-95',
          !responsive && 'h-[178px] w-[22.625rem]',
          responsive && 'lg:h-[178px] xl:w-[22.625rem]',
          'shadow-[0_4px_16px_0_rgba(14,16,22,0.08)] transition-shadow',
          canAccess && !isPreview && 'cursor-pointer hover:shadow-[0_8px_24px_0_rgba(14,16,22,0.24)] dark:hover:bg-neutral-90',
          isPreview && 'cursor-not-allowed dark:border dark:border-neutral-70',
          !canAccess && isPreview && 'border border-neutral-40',
          className,
        )}
        data-testid="product tile container"
        style={{ fontFamily }}
      >
        <div
          data-testid="product-tile-top"
          className={clsx(
            'mb-3 h-2 w-full',
            canAccess && !isPreview && 'group-hover:!bg-neutral-80 dark:group-hover:!bg-neutral-30',
            !canAccess && isPreview && 'bg-neutral-20 dark:bg-neutral-85',
          )}
          style={{ backgroundColor }}
        />
        <div className="flex items-center px-4 mb-3 h-[56px]">
          {isPreview ? (logoImageHref ? <div className={clsx('w-12 h-12 mr-3 shrink-0 flex items-center', !canAccess && 'opacity-50')}>
            <img
              alt={slug}
              className="max-w-100% max-h-12"
              src={logoImageHref}
            />
          </div> : null)
            :
            isDark() ?
              (logoImageDarkModeHref ? <div className={clsx('w-12 h-12 mr-3 shrink-0 flex items-center', !canAccess && 'opacity-50')}>
                <img
                  alt={slug}
                  className="max-w-100% max-h-12"
                  src={logoImageDarkModeHref}
                />
              </div> : null)
              :
              (logoImageHref ? <div className={clsx('w-12 h-12 mr-3 shrink-0 flex items-center', !canAccess && 'opacity-50')}>
                <img
                  alt={slug}
                  className="max-w-100% max-h-12 mr-3"
                  src={logoImageHref}
                />
              </div> : null)}
          <h3
            data-testid="product tile name"
            className={clsx(
              'line-clamp-2 xl:w-[11.75rem] text-neutral-98 break-words overflow-hidden font-semibold leading-6 flex-grow',
              responsive ? 'sm:text-body1 md:text-h4' : 'text-h4',
              canAccess ? 'text-neutral-98 dark:text-neutral-20' : 'text-neutral-50 dark:text-neutral-60'
            )}
          >
            {name?.substring(0, appDefaults.productsSettings.productNameMaxLength)}
          </h3>
          
        </div>
        <div
          data-testid="product tile description"
          className="flex items-start gap-4 px-4 mb-6 h-[66px]"
        >
          <div
            className={clsx(
              'w-full overflow-hidden line-clamp-3',
              responsive ? 'sm:text-body3-mobile md:text-body2' : 'text-body2',
              canAccess ? 'text-neutral-98 dark:text-neutral-20' : 'text-neutral-50 dark:text-neutral-60')}
          >
            {description?.substring(0, appDefaults.productsSettings.productDescriptionMaxLength)}
          </div>
          <div className="mt-auto ml-auto">
            <ProductTileAction
              product={product}
              userFullName={userFullName}
              accentColor={accentColor}
              portalName={portalName}
              loggedIn={loggedIn}
              isPreview={isPreview}
            />
          </div>
        </div>
      </div>
    </ProductWrapper>
  )
}